import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Button, Modal, Select, Switch, Tag } from 'antd';
import { LinkOutlined, ReloadOutlined } from '@ant-design/icons';
import DataTable from '@/components/data-table';
import TruncateTooltipCell from '@/components/data-table/TruncateTooltipCell';
import useQueryParamsContext from '@/contexts/query-params/useQueryParamsContext';
import { API } from '@/services/api.service';
import { date } from '@/app/suppliers/_components/format';
import { noop } from '@/app/sef-state/_components/helpers';
import { errorText } from './helpers';

const KIND_COLORS = { reply: 'green', auto: 'default', report: 'red' };

/** What arrived in the request's mailbox, and connecting what the platform could not match. */
export default function InboxPanel({ companyId, request, canWrite, onChanged }) {
  const { t } = useTranslation('partnerRequests');
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { queryParams, setQueryParams } = useQueryParamsContext();
  const [assigning, setAssigning] = useState(null);
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState(null);
  const requestId = request._id;
  const unmatched = queryParams.unmatched === 'true';
  const query = { offset: queryParams.offset, limit: queryParams.limit, ...(unmatched && { unmatched: 'true' }) };

  const list = useQuery({
    queryKey: ['partnerRequests', requestId, 'inbox', query],
    queryFn: () => API.partnerRequests.getInbox(companyId, requestId, query),
    placeholderData: keepPreviousData,
  });
  const candidates = useQuery({
    queryKey: ['partnerRequests', requestId, 'assign', search],
    queryFn: () => API.partnerRequests.getRecipients(companyId, requestId, { name: search, limit: 20 }),
    enabled: Boolean(assigning),
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['partnerRequests', requestId] });
    onChanged();
  };
  const check = useMutation({
    mutationFn: () => API.partnerRequests.checkMailbox(companyId, requestId),
    onSuccess: (result) => {
      message.success(t('inbox.checked', { added: result.added, unmatched: result.unmatched }));
      refresh();
    },
  });
  const assign = useMutation({
    mutationFn: () => API.partnerRequests.assignMessage(companyId, requestId, assigning._id, picked),
    onSuccess: () => {
      setAssigning(null);
      setPicked(null);
      setSearch('');
      refresh();
    },
  });

  const c = (key) => t(`inbox.columns.${key}`);
  const columns = [
    { title: c('date'), dataIndex: 'date', width: 110, render: date },
    { title: c('from'), dataIndex: 'from', width: 240, render: (v) => <TruncateTooltipCell label={v} /> },
    { title: c('subject'), dataIndex: 'subject', width: 340, render: (v) => <TruncateTooltipCell label={v} /> },
    { title: c('attachments'), dataIndex: 'attachments', width: 90, align: 'right' },
    {
      title: c('kind'),
      dataIndex: 'kind',
      width: 160,
      render: (v) => <Tag color={KIND_COLORS[v]}>{t(`inbox.kinds.${v}`)}</Tag>,
    },
    {
      title: c('recipient'),
      dataIndex: 'recipient',
      width: 280,
      render: (v, r) =>
        v ? (
          <TruncateTooltipCell label={v.name} />
        ) : canWrite ? (
          <Button shape="round" size="small" icon={<LinkOutlined />} onClick={() => setAssigning(r)}>
            {t('inbox.assign')}
          </Button>
        ) : (
          <Tag color="gold">{t('inbox.unmatched')}</Tag>
        ),
    },
  ];
  const { data } = list;
  const error = list.error || check.error;

  return (
    <div className="grid gap-3">
      {!request.mailboxConfigured && <Alert type="warning" showIcon message={t('errors.mailbox_not_configured')} />}
      {error && <Alert type="error" showIcon message={errorText(t, error)} />}
      {request.mailboxCheckedAt && (
        <p className="text-sm opacity-70">
          {t('inbox.lastCheck', { at: new Date(request.mailboxCheckedAt).toLocaleString('sr-RS') })}
        </p>
      )}
      <DataTable
        rowKey="_id"
        title={`${t('inbox.title')}${data ? ` · ${data.count}` : ''}`}
        data={data?.items || []}
        total={data?.count || 0}
        status={list.isLoading ? 'pending' : list.error ? 'error' : 'success'}
        columns={columns}
        offset={queryParams.offset}
        limit={queryParams.limit}
        onChangePagination={noop}
        tableLayout="fixed"
        scroll={{ x: columns.reduce((s, col) => s + col.width, 0) }}
        size="small"
        headerChildren={
          <>
            <span className="flex items-center gap-2 text-sm text-white">
              <Switch
                size="small"
                checked={unmatched}
                onChange={(on) => setQueryParams({ unmatched: on ? 'true' : undefined, offset: 1 })}
              />
              {t('inbox.onlyUnmatched')}
            </span>
            {canWrite && (
              <Button
                type="default"
                shape="round"
                className="btn-default h-8 text-black"
                icon={<ReloadOutlined className="text-zinc-400" />}
                loading={check.isPending}
                disabled={!request.mailboxConfigured || !request.firstSentAt}
                onClick={() => check.mutate()}
              >
                {t('inbox.check')}
              </Button>
            )}
          </>
        }
      />
      <Modal
        open={Boolean(assigning)}
        title={t('inbox.assignTitle')}
        onCancel={() => setAssigning(null)}
        footer={[
          <Button key="c" shape="round" onClick={() => setAssigning(null)}>
            {t('cancel')}
          </Button>,
          <Button
            key="o"
            shape="round"
            type="primary"
            disabled={!picked}
            loading={assign.isPending}
            onClick={() => assign.mutate()}
          >
            {t('inbox.assign')}
          </Button>,
        ]}
      >
        {assign.isError && <Alert className="mb-2" type="error" showIcon message={errorText(t, assign.error)} />}
        <p className="mb-2 text-sm opacity-70">
          {assigning?.from} · {assigning?.subject}
        </p>
        <Select
          className="w-full"
          showSearch
          filterOption={false}
          value={picked}
          placeholder={t('inbox.searchPartner')}
          loading={candidates.isFetching}
          onSearch={setSearch}
          onChange={setPicked}
          options={(candidates.data?.items || []).map((r) => ({ value: r._id, label: `${r.name} · ${r.pib || t('noPib')}` }))}
        />
      </Modal>
    </div>
  );
}
